import { withTenderTime } from "../../../context/TenderTimeProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./OrderInfoClient.module.scss";
import { File, Order, User } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";
import Loading from "../../shared/Loading/Loading";
import OrderInfoClientHeader from "./components/OrderInfoClientHeader";
import OrderInfoClientStepper from "./components/OrderInfoClientStepper";
import OrderInfoClientTabs from "./components/OrderInfoClientTabs";
import ActionsTab from "./components/tabs/ActionsTab";
import RouteTab from "./components/tabs/RouteTab";
import ExecutorTab from "./components/tabs/ExecutorTab";
import DocumentsTab from "./components/tabs/DocumentsTab";
import OrdersChatHub from "../../shared/chat/OrdersChatHub";
import { useOrderRealtimeEvents } from "../../../hooks/useOrderRealtimeEvents";
import {
  CHAT_REFRESH_TARGETS,
  ORDER_DETAIL_REFRESH_TARGETS,
} from "../../../services/orderRealtimeEvents";
import { useChatUnreadRefresh } from "../../../hooks/useChatUnreadRefresh";
import { loadOrderMapData } from "../utils/loadOrderMapData";

const normalizeOrderDates = (order) => {
  const normalizedOrder = { ...order };

  if (normalizedOrder.shippingDate) {
    const [date] = normalizedOrder.shippingDate.split("T");
    const timeStart = String(normalizedOrder.shippingHourFrom).padStart(2, "0");
    const timeEnd = String(normalizedOrder.shippingHourTo).padStart(2, "0");
    normalizedOrder.shippingDate = `${date}T${timeStart}:00:00`;
    normalizedOrder.shippingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  if (normalizedOrder.unloadingDate) {
    const [date] = normalizedOrder.unloadingDate.split("T");
    const timeStart = String(normalizedOrder.unloadingHourFrom).padStart(2, "0");
    const timeEnd = String(normalizedOrder.unloadingHourTo).padStart(2, "0");
    normalizedOrder.unloadingDate = `${date}T${timeStart}:00:00`;
    normalizedOrder.unloadingDateEnd = `${date}T${timeEnd}:00:00`;
  }

  return normalizedOrder;
};

const createOrderMapState = (orderId = null, isLoadingMap = false) => ({
  orderId,
  cityFrom: null,
  cityTo: null,
  routeData: null,
  coordinate: [],
  isLoadingMap,
});

const OrderInfoClient = ({
  isOpen,
  setIsOpen,
  orderId,
  companyName,
  onForceUpdate,
  onEditOrder,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { user } = useSelector((state) => state);
  const company = useSelector((state) => state.user.company);

  const [getOrder, setOrder] = useState(null);
  const [cargoDetails, setCargoDetails] = useState();
  const [recipient, setRecipient] = useState();
  const [bookingDate, setBookingDate] = useState();
  const [statusHistory, setStatusHistory] = useState();
  const [mapState, setMapState] = useState(() => createOrderMapState());
  const [listDocDriver, setListDocDriver] = useState([]);
  const [listFilesOrder, setListFilesOrder] = useState([]);
  const [orderViews, setOrderViews] = useState([]);
  const [listSuggestedPrice, setListSuggestedPrice] = useState([]);
  const [complexTenderBids, setComplexTenderBids] = useState([]);
  const [driverPrices, setDriverPrices] = useState([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("actions");
  const [isOpenChatModal, setIsOpenChatModal] = useState(false);
  const orderRequestIdRef = useRef(0);
  const mapRequestIdRef = useRef(0);
  const tenderRequestIdRef = useRef(0);
  const dependentOrderIdRef = useRef(null);
  const {
    refreshUnreadMessages,
    refreshUnreadOnClose,
    scheduleUnreadRefresh,
  } = useChatUnreadRefresh({
    getUnreadMessages: Order.getUnreadMessages,
    orderId,
    unreadMessagesCount,
    setUnreadMessagesCount,
  });

  const fetchDocuments = () => {
    if (!getOrder?.id) return;
    Promise.all([
      File.getOrderDocsDriverPhotos(getOrder.id).catch(() => ({
        status: 200,
        data: [],
      })),
      File.getFilesByOrder(getOrder.id, 5),
      File.getFilesByOrder(getOrder.id, 7),
    ])
      .then(([driverPhotosResponse, r5, r7]) => {
        const driverPhotos =
          driverPhotosResponse?.status === 200 ||
          driverPhotosResponse?.status === 201
            ? driverPhotosResponse?.data || []
            : [];
        setListFilesOrder(
          [
            ...(r5?.data || []),
            ...(r7?.data || []),
            ...driverPhotos,
          ].reverse(),
        );
      })
      .catch((e) => console.log(e));
  };

  const fetchOrderData = useCallback(async ({ silent = false } = {}) => {
    if (!orderId || !isOpen) return;

    const requestId = orderRequestIdRef.current + 1;
    orderRequestIdRef.current = requestId;
    if (!silent) {
      setLoading(true);
    }

    try {
      const { data } = await Order.getOrderById(orderId, currentLanguage);

      if (requestId !== orderRequestIdRef.current) return;

      setOrder(normalizeOrderDates(data));
    } catch (error) {
      if (requestId !== orderRequestIdRef.current) return;

      console.error(error);
      toast.error(t("common.genericError"));
    } finally {
      if (requestId === orderRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentLanguage, isOpen, orderId, t]);

  useOrderRealtimeEvents(({ target, data }) => {
    const isMatchingOrder =
      data?.orderId != null && String(data.orderId) === String(orderId);

    if (!isOpen || !isMatchingOrder) return;

    if (CHAT_REFRESH_TARGETS.has(target)) {
      refreshUnreadMessages();
      return;
    }

    if (ORDER_DETAIL_REFRESH_TARGETS.has(target)) {
      fetchOrderData({ silent: true });
    }
  });

  useEffect(() => {
    const nextOrderId = getOrder?.id ?? null;
    const isSameOrder =
      dependentOrderIdRef.current !== null &&
      nextOrderId !== null &&
      String(dependentOrderIdRef.current) === String(nextOrderId);

    dependentOrderIdRef.current = nextOrderId;

    if (!isSameOrder) {
      setUnreadMessagesCount(0);
      setOrderViews([]);
      setListSuggestedPrice([]);
      setComplexTenderBids([]);
      setDriverPrices([]);
    }

    const mapRequestId = mapRequestIdRef.current + 1;
    mapRequestIdRef.current = mapRequestId;
    const tenderRequestId = tenderRequestIdRef.current + 1;
    tenderRequestIdRef.current = tenderRequestId;

    if (!getOrder) {
      setMapState(createOrderMapState());
      return undefined;
    }

    if (!isSameOrder) {
      setMapState(createOrderMapState(nextOrderId, true));
    }

    loadOrderMapData(getOrder)
      .then(({ data, errors }) => {
        if (mapRequestId !== mapRequestIdRef.current) return;

        errors.forEach(({ requestName, error }) => {
          console.error(`Failed to load order map ${requestName}`, error);
        });

        setMapState((currentState) => {
          const canPreserveCurrentState =
            isSameOrder && String(currentState.orderId) === String(nextOrderId);
          const nextState = canPreserveCurrentState
            ? { ...currentState }
            : createOrderMapState(nextOrderId);

          Object.assign(nextState, data, { orderId: nextOrderId, isLoadingMap: false });
          return nextState;
        });
      })
      .catch((error) => {
        if (mapRequestId !== mapRequestIdRef.current) return;

        console.error("Failed to load order map data", error);
        setMapState((currentState) => ({ ...currentState, isLoadingMap: false }));
      });

    Order.getCargo(getOrder.cargoId)
      .then((r) => setCargoDetails(r.status === 200 ? r.data : {}))
      .catch((e) => console.log(e));

    Order.getRecipient(getOrder.recipientId)
      .then((r) => setRecipient(r.status === 200 ? r.data : {}))
      .catch((e) => console.log(e));

    User.getHistory(getOrder.id)
      .then((res) => res.data)
      .then((data) => {
        setBookingDate(data[3]?.createdAt);
        setStatusHistory(data);
      })
      .catch((e) => console.log(e));

    Order.getUnreadMessages(getOrder.id)
      .then((r) => {
        if (r.status === 200) setUnreadMessagesCount(r.data?.count || 0);
      })
      .catch((e) => console.log(e));

    if (getOrder.driverId) {
      File.getDocList(getOrder.driverId)
        .then((r) => {
          const filtered = r.data.filter((d) => d.type !== 8);
          const hasTypes = [4, 5, 7].some((t) => filtered.some((d) => d.type === t));
          setListDocDriver(filtered);
          if (!hasTypes && getOrder.vehicleId) {
            File.getDocListVehicle(getOrder.vehicleId)
              .then((vr) => setListDocDriver([...filtered, ...vr.data]))
              .catch((e) => console.log(e));
          }
        })
        .catch((e) => console.log(e));
    } else if (getOrder.vehicleId) {
      File.getDocListVehicle(getOrder.vehicleId)
        .then((r) => setListDocDriver(r.data))
        .catch((e) => console.log(e));
    } else {
      setListDocDriver([]);
    }

    if (getOrder.companyId === company?.id) {
      Order.getOrderViewById(getOrder.id)
        .then((r) => setOrderViews(r.status === 200 ? r.data : []))
        .catch((e) => { console.log(e); setOrderViews([]); });
    }

    if (user.type === "Client" || getOrder.companyId === company?.id) {
      if (getOrder?.isComplexTender) {
        Order.getComplexOrderBidList(getOrder.id)
          .then((response) => {
            if (tenderRequestId !== tenderRequestIdRef.current) return;
            setComplexTenderBids(response?.data || []);
            setListSuggestedPrice([]);
            setDriverPrices([]);
          })
          .catch((e) => {
            if (tenderRequestId === tenderRequestIdRef.current) console.log(e);
          });
      } else {
        Order.getListSuggestedPrice(getOrder.id)
          .then((response) => {
            if (tenderRequestId !== tenderRequestIdRef.current) return;

            const allPrices = response?.data?.rows || [];
            const drivers = allPrices.filter((p) => p.userType === "driver");
            const nonDriverPrices = allPrices.filter((p) => p.userType !== "driver");
            setListSuggestedPrice(nonDriverPrices);
            setComplexTenderBids([]);
            setDriverPrices(drivers);
          })
          .catch((e) => {
            if (tenderRequestId === tenderRequestIdRef.current) console.log(e);
          });
      }
    }

    Promise.all([
      File.getOrderDocsDriverPhotos(getOrder.id).catch(() => ({
        status: 200,
        data: [],
      })),
      File.getFilesByOrder(getOrder.id, 5),
      File.getFilesByOrder(getOrder.id, 7),
      File.getFilesByOrder(getOrder.id, 0),
    ])
      .then(([driverPhotosResponse, r5, r7]) => {
        const driverPhotos =
          driverPhotosResponse?.status === 200 ||
          driverPhotosResponse?.status === 201
            ? driverPhotosResponse?.data || []
            : [];
        setListFilesOrder(
          [
            ...(r5?.data || []),
            ...(r7?.data || []),
            ...driverPhotos,
          ].reverse(),
        );
      })
      .catch((e) => console.log(e));

    return () => {
      if (mapRequestId === mapRequestIdRef.current) {
        mapRequestIdRef.current += 1;
      }
      if (tenderRequestId === tenderRequestIdRef.current) {
        tenderRequestIdRef.current += 1;
      }
    };
  }, [getOrder]);

  useEffect(() => {
    setUnreadMessagesCount(0);

    if (!orderId) {
      setOrder(null);
      setLoading(false);
      return undefined;
    }

    if (!isOpen) return undefined;

    setOrder((currentOrder) => (currentOrder?.id === orderId ? currentOrder : null));
    fetchOrderData();

    return () => {
      orderRequestIdRef.current += 1;
    };
  }, [fetchOrderData, isOpen, orderId]);

  const handleOrderDataChanged = useCallback(() => {
    fetchOrderData();
    onForceUpdate?.();
  }, [fetchOrderData, onForceUpdate]);

  const executorCount = getOrder?.drivers?.length || 0;
  const documentsCount = listFilesOrder.length;
  const isMapStateForCurrentOrder =
    getOrder?.id != null && String(mapState.orderId) === String(getOrder.id);
  const currentMapState = isMapStateForCurrentOrder
    ? mapState
    : createOrderMapState(getOrder?.id ?? null, Boolean(getOrder));
  const hasBids = getOrder?.isComplexTender
    ? complexTenderBids.length > 0
    : listSuggestedPrice.length > 0 || driverPrices.length > 0;

  const handleOpenChat = () => {
    setIsOpenChatModal(true);
    scheduleUnreadRefresh();
  };

  const handleCloseChat = () => {
    setIsOpenChatModal(false);
    refreshUnreadOnClose();
  };

  return (
    <>
    <div
      className={cn(style.overlay, { [style.overlay_open]: isOpen })}
      onClick={() => setIsOpen(false)}
    />
    <div className={cn(style.wrapper, { [style.open]: isOpen })}>
      <OrderInfoClientHeader
        item={getOrder}
        setIsOpen={setIsOpen}
        onOpenChat={handleOpenChat}
        unreadCount={unreadMessagesCount}
        onForceUpdate={onForceUpdate}
        orderViews={orderViews}
        hasBids={hasBids}
        onOrderDataChanged={handleOrderDataChanged}
        onEditOrder={onEditOrder}
      />

      {isLoading && <Loading borderRadius="0" />}

      <OrderInfoClientStepper
        status={getOrder?.status}
        assignmentMethod={getOrder?.assignmentMethod}
        transportationType={getOrder?.transportationType}
      />

      <OrderInfoClientTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        executorCount={executorCount}
        documentsCount={documentsCount}
      />

      <div className={style.content}>
        {activeTab === "actions" && (
          <ActionsTab
            key={orderId}
            order={getOrder}
            cargoDetails={cargoDetails}
            fetchOrderData={fetchOrderData}
            onForceUpdate={onForceUpdate}
            refreshDocuments={fetchDocuments}
            onClose={() => setIsOpen(false)}
            statusHistory={statusHistory}
            listSuggestedPrice={listSuggestedPrice}
            driverPrices={driverPrices}
            complexTenderBids={complexTenderBids}
          />
        )}
        {activeTab === "route" && (
          <RouteTab
            order={getOrder}
            cargoDetails={cargoDetails}
            companyName={companyName}
            recipient={recipient}
            cityFrom={currentMapState.cityFrom}
            cityTo={currentMapState.cityTo}
            routeData={currentMapState.routeData}
            isLoadingMap={currentMapState.isLoadingMap}
            coordinateTruck={currentMapState.coordinate}
          />
        )}
        {activeTab === "executor" && (
          <ExecutorTab order={getOrder} />
        )}
        {activeTab === "documents" && (
          <DocumentsTab
            order={getOrder}
            listFilesOrder={listFilesOrder}
            refreshDocuments={fetchDocuments}
          />
        )}
      </div>
    </div>

    {isOpenChatModal && getOrder && (
      <OrdersChatHub
        isOpen={isOpenChatModal}
        orderData={getOrder}
        orderId={getOrder.id}
        orderNumber={getOrder.number}
        onClose={handleCloseChat}
      />
    )}
    </>
  );
};

export default withTenderTime(OrderInfoClient);
